/* eslint-disable */
import {
  AreaChartOutlined,
  BookOutlined,
  CloseOutlined,
  DownloadOutlined, EditOutlined, FileDoneOutlined, ImportOutlined,
  PlusOutlined,
  RollbackOutlined,
  SaveOutlined,
  WarningOutlined
} from '@ant-design/icons';
import {
  Button, Card,
  InputNumber,
  message,
  Modal,
  Space, Spin, Table, TableProps, Tooltip, Typography
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import {
  Controller, FormProvider,
  useForm
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import { AxiosError } from 'axios';
import { DropdownElement } from 'common/components/DropdownType';
import SearchInput from 'common/components/PageTable/searchInput';
import { createAttendanceRecordsService, getAllAttendanceRecordsService, updateNoteAttendanceRecordsService } from 'common/services/extends/attendanceRecords';
import { CreateAttendanceRecordsParams, UpdateAttendanceRecordsParams } from 'common/services/extends/attendanceRecords/types';
import { approveStaffProjectService, getPermissionFromProjectByIdService } from 'common/services/extends/project';
import { ApproveStaffParams } from 'common/services/extends/project/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { detectError } from 'common/utils/functions';
import { dateKeyRegex } from 'common/utils/schemas';
import roles, { getPermission } from 'configs/roles';
import ProjectStaffJoinedModal from './ProjectStaffJoinedModal';

interface DataType {
  id: number,
  name?: string,
  cccd: string,
  note?: string,
  position?: OptionType,
  [key: string]: any;
  totalShiftCount: number;
  totalWorkingDate: number,
  workingDateWarning: boolean;
}

type TimeSheetTable = {
  id: number,
  name: string,
  cccd: string,
  note: string,
  position?: OptionType,
  workingDateWarning: boolean;
  totalWorkingDate: number,
  totalShiftCount: number;
  dates: Array<OptionType>
};

type TimeSheetTableProps = {
  monthPayroll: string;
  approved?: boolean;
  queryKey: (string | number)[];
  onHandleDownload?: () => void;
};

interface FormValues {
  users: DataType[];
}

const calTotal = (editStaff: DataType | null) => {
  if (!editStaff) return;
  const includesDates = Object.entries(editStaff)
    .filter(([key,]) => dateKeyRegex.test(key));
  const totals = includesDates?.reduce((prev, current) => {
    if (!current[1]) return prev;
    return prev + current[1];
  }, 0);
  return totals;
};

const TimeSheetTable: React.FC<TimeSheetTableProps> = ({
  approved,
  monthPayroll,
  onHandleDownload
}) => {
  const rolesUser = useAppSelector((state) => state.auth.roles);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId') || '';
  const staffFullTimeId = searchParams.get('staffFullTimeId') || '';
  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';

  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [isOpenProjectStaffJoined, setIsOpenProjectStaffJoined] = useState<{
    isOpen: boolean,
    staffId: number,
  }>({
    isOpen: false,
    staffId: -1
  });
  const [keyword, setKeyword] = useState('');

  const [editStaff, setEditStaff] = useState<DataType | null>(null);
  const [indexEdit, setIndexEdit] = useState<number>(-1);

  const method = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      users: []
    }
  });

  const { isDirty } = method.formState;

  const methodNote = useForm<{ note: string, id: number | null }>({
    defaultValues: {
      note: '',
      id: null
    },
    mode: 'onChange',
  });

  const { isLoading: isLoadingPermission, data: permission } = useQuery(
    ['project-permission-detail', projectId, staffFullTimeId],
    () => getPermissionFromProjectByIdService(
      projectId,
      {
        staffFullTimeId
      }
    ),
    {
      enabled: !!staffFullTimeId && !!projectId,
    }
  );

  const queryKeyPayrollPeriod = ['attendance-records-detail', payrollPeriodId, keyword];

  const { data, isLoading: loadingAttendance, refetch, isFetching } = useQuery(
    queryKeyPayrollPeriod,
    () => getAllAttendanceRecordsService({
      _payrollPeriodId: payrollPeriodId,
      ...(keyword ? { keyword } : undefined)
    }),
    {
      onSuccess(res) {
        method.reset({
          users: res.map((i, index) => {
            const dates = i.attendanceData.reduce((acc: any, curr) => {
              acc[curr.date] = curr.shiftCount;
              return acc;
            }, {});
            return {
              key: index + 1,
              id: i.staffData.id,
              cccd: i.staffData.idCardNumber,
              note: i.staffData.note,
              name: i.staffData.fullname,
              workingDateWarning: i.staffData.workingDateWarning,
              totalShiftCount: i.staffData.totalShiftCount || 0,
              totalWorkingDate: i.staffData.totalWorkingDate || 0,
              position: i.position ? {
                label: i.position.name,
                value: i.position.id
              } as OptionType : undefined,
              ...dates
            };
          })
        });
      },
      enabled: !!payrollPeriodId && getPermission(rolesUser, roles.ATTENDANCE_RECORD_INDEX),
    }
  );

  const { mutate: createAttendanceRecordsMutate, isLoading: createLoading } = useMutation(
    ['createAttendanceRecords'],
    async (params: CreateAttendanceRecordsParams) => createAttendanceRecordsService(params),
    {
      onSuccess: (res) => {
        if (editStaff) {
          const {
            position,
            totalShiftCount,
          } = editStaff;

          Object.entries(editStaff)
            .filter(([key,]) => dateKeyRegex.test(key))
            .forEach(([key, value]) => method.setValue(`users.${indexEdit}.${key}`, value));
          method.setValue(`users.${indexEdit}.position`, position);
          method.setValue(`users.${indexEdit}.workingDateWarning`, res.workingDateWarning);
          method.setValue(`users.${indexEdit}.totalShiftCount`, calTotal(editStaff) || totalShiftCount);
        }

        message.success(t('message.updateSuccess'));
        setEditStaff(null);
        setIndexEdit(-1);
        method.clearErrors();
      },
      onError: (error: ErrorResponse[]) => {
        if (error.length > 0) {
          let isThreshold = false;
          error.forEach(async (e) => {
            if (e.code === 'integer') {
              const indexError = e.field.split('.')?.[1];
              if (indexError) {
                const dateKey = data?.[0].attendanceData.find((_, attIndex) => attIndex === Number(indexError));

                if (dateKey) {
                  method.setError(
                    `users.${indexEdit}.${dateKey.date}`,
                    { message: 'Giá trị không hợp lệ', type: 'validate', },
                    {
                      shouldFocus: true
                    }
                  );
                }
              }
            }
            if (e.code === 'max' || e.code === 'shiftLimitExceeded') {
              isThreshold = true;
              const indexError = e.field.split('.')?.[1];
              if (indexError) {
                const dateKey = data?.[0].attendanceData.find((_, attIndex) => attIndex === Number(indexError));

                if (dateKey) {
                  method.setError(
                    `users.${indexEdit}.${dateKey.date}`,
                    { message: t('message.overNumberOfShift'), type: 'validate', },
                    {
                      shouldFocus: true
                    }
                  );
                }
              }
            }
          });
          if (isThreshold) {
            message.error(<div dangerouslySetInnerHTML={{ __html: t('message.overNumberOfShift') }} />);
          }
        } else {
          message.error(t('message.anErrorHasOccurred'));
        }
      }
    }
  );

  const { mutate: updateNoteAttendanceRecordsMutate, isLoading: updateNoteLoading } = useMutation(
    ['updateNoteAttendanceRecords'],
    async (params: UpdateAttendanceRecordsParams) => updateNoteAttendanceRecordsService(params),
    {
      onSuccess: (_, variables) => {
        method.setValue(`users.${indexEdit}.note`, variables.note);
        message.success(t('message.updateSuccess'));
        // queryClient.invalidateQueries(queryKey);
      },
      onError: () => {
        message.error(t('message.updateError'));
      },
      onSettled: () => {
        setIsNoteOpen(false);
        if (!editStaff) {
          setIndexEdit(-1);
        }
      }
    }
  );

  const { mutate: removeMutate, isLoading: isLoadingRemove } = useMutation(
    ['remove-staffs'],
    (params: ApproveStaffParams) => approveStaffProjectService(params),
    {
      onSuccess: () => {
        message.success(t('message.rejectStaffSuccess'));
        refetch();
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const handleRevoke = (ids: number[]) => {
    removeMutate({
      payrollPeriodId,
      staffIds: ids,
      approved: false,
    });
  };

  // const updateField = (field: any, valueParams?: string | OptionType) => {
  //   const clone = { ...editStaff } as any;
  //   if (typeof valueParams === 'object') {
  //     clone[field] = valueParams;
  //   } else {
  //     clone[field] = valueParams ? Number(valueParams) : 0;
  //   }
  //   setEditStaff(clone);
  // };

  const actionColumn: TableProps<DataType>['columns'] = useMemo(() => {
    return [
      {
        title: t('system.action'),
        key: 'action',
        width: 50,
        align: 'center',
        fixed: 'right',
        render: (_name: string, data: DataType, rowIndex) => (
          <Space key={data.id}>
            {
              editStaff && editStaff.id === data.id ? (
                <>
                  <Tooltip title="Lưu">
                    <Button
                      icon={<SaveOutlined />}
                      onClick={async () => {
                        const isTigger = await method.trigger();
                        const errors = method.formState.errors;

                        if (!isTigger || (errors.users && errors.users?.length > 0)) return;
                        if (payrollPeriodId) {
                          const submitData: CreateAttendanceRecordsParams = {
                            payrollPeriodId: Number(payrollPeriodId),
                            staffId: data.id,
                            positionId: Number(data.position?.value),
                            shifts: Object.entries(data)
                              .filter(([key,]) => dateKeyRegex.test(key))
                              .map(([key, value]) => {
                                return ({
                                  date: key,
                                  shiftCount: value || 0
                                })
                              })
                          };
                          method.clearErrors();
                          createAttendanceRecordsMutate(submitData);
                        }
                      }}
                    >
                      Lưu
                    </Button>
                  </Tooltip>
                  <Tooltip title="Hủy">
                    <Button
                      icon={<CloseOutlined />}
                      onClick={() => {
                        if (isDirty) {
                          refetch();
                        }
                        setEditStaff(null);
                      }}
                    >
                      Hủy
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <Space>
                  {
                    !approved && (
                      <>
                        <Tooltip title="Chỉnh sửa">
                          <Button
                            icon={<EditOutlined />}
                            onClick={() => {
                              setEditStaff(data);
                              setIndexEdit(rowIndex);
                            }}
                            disabled={!!editStaff || !getPermission(rolesUser, roles.ATTENDANCE_RECORD_STORE) || !permission?.canDo}
                          />
                        </Tooltip>
                        <Tooltip title="Xoá nhân sự">
                          <Button
                            size="small"
                            danger
                            disabled={!getPermission(rolesUser, roles.PROJECT_APPROVED_STAFF) || !permission?.canDo}
                            onClick={() => Modal.confirm({
                              className: 't-pagetable_deleteRecordModal',
                              autoFocusButton: 'cancel',
                              okText: t('system.ok'),
                              cancelText: t('system.cancel'),
                              cancelButtonProps: {
                                type: 'primary',
                              },
                              okButtonProps: {
                                type: 'default',
                              },
                              title: t('message.confirmDeleteStaff'),
                              onOk: () => {
                                handleRevoke?.([data.id]);
                              },
                            })}
                          >
                            <RollbackOutlined />
                          </Button>
                        </Tooltip>
                      </>
                    )
                  }
                  <Tooltip title="Chương trình tham gia">
                    <Button
                      type="primary"
                      disabled={!(getPermission(rolesUser, roles.PAYROLL_PERIOD_STAFF_JOIN_IN_PAYROLL) && permission?.canDo)}
                      icon={<AreaChartOutlined />}
                      onClick={() => {
                        setIsOpenProjectStaffJoined({
                          isOpen: true,
                          staffId: data.id
                        });
                      }}
                    />
                  </Tooltip>
                </Space>
              )
            }
          </Space>
        ),
      },
    ];
  }, [editStaff, data, payrollPeriodId, isDirty, approved, rolesUser, permission?.canDo]);

  const dateColumn: TableProps<DataType>['columns'] = useMemo(() => {
    if (data && data.length > 0) {
      return data[0].attendanceData.map((i) => ({
        title: (<div dangerouslySetInnerHTML={{
          __html: `
              <p>${dayjs(i.date).format('DD/MM')}</p>
              <p style="font-size: 12px; font-style: italic; font-weight: 400;">${dayjs(i.date).format('dddd')}</p>
        `}} />),
        dataIndex: i.date,
        key: i.date,
        align: 'center',
        render: (_value: any, _record, rowIndex) => {
          if (editStaff && editStaff.id === _record?.id) {
            return (
              <Space direction="vertical">
                <Controller
                  name={`users.${rowIndex}.${i.date}`}
                  control={method.control}
                  rules={{
                    validate: (value) => {
                      // Cho phép null/undefined là hợp lệ (tuỳ trường hợp bạn có muốn bắt buộc nhập không)
                      if (value === null || value === undefined || value === '') return true;

                      const num = Number(value);
                      const isValid = Number.isFinite(num) && (num * 10) % 5 === 0;

                      return isValid || t('message.enterInteger');
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <Tooltip title={error?.message ? <div dangerouslySetInnerHTML={{ __html: error.message?.toString() || '' }} /> : ''}>
                      <Space direction="vertical">
                        <InputNumber
                          value={field.value}
                          onChange={field.onChange}
                          size="middle"
                          type="text"
                          min={0}
                          step={0.5}
                          max={4}
                          style={error?.message ? { border: '1px solid red' } : {}}
                          onKeyDown={(e) => {
                            if (e.key === ',' || e.key === 'Decimal') {
                              e.preventDefault();
                            }
                          }}
                        />
                        {
                          error?.message && (
                            <Typography.Text type="danger">
                              <div dangerouslySetInnerHTML={{ __html: error.message?.toString() || '' }} />
                            </Typography.Text>
                          )
                        }
                      </Space>
                    </Tooltip>
                  )}
                />
              </Space>
            )
          }
          return _value;
        }
      }));
    }
    return [];
  }, [data, editStaff]);

  const columns: TableProps<DataType>['columns'] = [
    {
      title: 'STT',
      dataIndex: 'key',
      fixed: 'left',
      width: 50,
      align: 'center'
    },
    {
      title: t('timesheet.fullnameStaff'),
      dataIndex: 'name',
      width: 170,
      key: 'name',
      fixed: 'left',
      render: (_value: any, _record) => {
        return (
          <Space>
            {_value}
            {
              _record.workingDateWarning && (
                <Tooltip
                  title={<div dangerouslySetInnerHTML={{
                    __html: `
                      <div>${t('salary.didYou')}</div>
                      <ul class="salary_warning-ul">
                        ${_record.workingDateWarning ? `<li>${t('timesheet.exceedingTheDayTimekeepingThreshold')}</li>` : ''}
                      </ul>
                `}} />}
                >
                  <WarningOutlined size={30} style={{ color: "#ff3b30" }} />
                </Tooltip>
              )
            }
          </Space>
        )
      }
    },
    {
      title: t('system.cccd'),
      dataIndex: 'cccd',
      width: 100,
      key: 'cccd',
      fixed: 'left',
    },
    {
      title: t('system.position'),
      dataIndex: 'position',
      key: 'position',
      width: 100,
      fixed: 'left',
      align: 'center',
      render: (_value, record, rowIndex) => {
        if (editStaff && editStaff.id === record.id) {
          return (
            <div style={{ position: 'relative', zIndex: 100 }}>
              <Controller
                name={`users.${rowIndex}.position`}
                control={method.control}
                render={({ field, fieldState: { error } }) => (
                  <DropdownElement
                    type="position"
                    locale="vi"
                    isGetOption
                    value={field.value}
                    onChange={field.onChange}
                    isShowSearch
                    size="large"
                    allowClear={false}
                    noMargin
                    error={error?.message}
                  />
                )}
              />
            </div>
          )
        }
        return _value?.label || '';
      }
    },
    {
      title: t('system.total'),
      dataIndex: 'totalShiftCount',
      key: 'totalShiftCount',
      width: 50,
      fixed: 'left',
      align: 'center',
      render: (_value: any, _record) => {
        if (editStaff && editStaff.id === _record?.id) {
          return calTotal(editStaff);
        }
        return _value;
      }
    },
    ...dateColumn,
    {
      title: t('salary.note'),
      width: 50,
      key: 'action',
      align: 'center',
      fixed: 'right',
      render: (_value: string, data: DataType, index) => (
        <Space>
          <Tooltip title={t('salary.note')}>
            <Button
              disabled={!getPermission(rolesUser, roles.ATTENDANCE_RECORD_UPDATE_NOTE)}
              icon={data.note ? <FileDoneOutlined style={{ color: '#f69320' }} /> : <BookOutlined />}
              onClick={() => {
                setIndexEdit(index);
                setIsNoteOpen(true);
                methodNote.reset({
                  note: data.note || '',
                  id: data.id
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
    ...actionColumn
  ];

  const handleSubmitNote = () => {
    const formData = methodNote.getValues();

    updateNoteAttendanceRecordsMutate(
      {
        payrollPeriodId: Number(payrollPeriodId),
        note: formData.note || '',
        staffId: formData.id || 0
      }
    );
  };

  return (
    <div className="time-keeping-table">
      <Spin spinning={createLoading || updateNoteLoading || loadingAttendance || isLoadingRemove || isFetching || isLoadingPermission}>
        <Card
          title={(
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div className="time-keeping-table__header-left">
                <Typography.Title level={4}>
                  {t('timesheet.timeSheetTable')}
                </Typography.Title>
              </div>
              <div style={{ marginLeft: 16 }}>
                <SearchInput
                  handleSearch={setKeyword}
                />
              </div>
            </div>
          )}
          extra={(
            <Space>
              {
                !approved && (
                  <>
                    <Button
                      type="primary"
                      icon={(<PlusOutlined />)}
                      onClick={() => navigate(`${ROUTE_PATHS.SALARY_PROGRAM_DETAIL}?projectId=${projectId}&staffFullTimeId=${staffFullTimeId}&payrollPeriodId=${payrollPeriodId}`)}
                      disabled={!getPermission(rolesUser, roles.PROJECT_APPROVED_STAFF) || !permission?.canDo}
                    >
                      {t('timesheet.addStaff')}
                    </Button>
                    <Button
                      size="middle"
                      type="default"
                      icon={(<DownloadOutlined />)}
                      onClick={onHandleDownload}
                      disabled={!getPermission(rolesUser, roles.ATTENDANCE_RECORD_EXPORT_INDEX) || !permission?.canDo}
                    >
                      {t('timesheet.download')}
                    </Button>
                    <Button
                      size="middle"
                      type="default"
                      icon={(<ImportOutlined />)}
                      disabled={!getPermission(rolesUser, roles.ATTENDANCE_RECORD_IMPORT) || !permission?.canDo}
                      onClick={() => navigate(`${ROUTE_PATHS.ATTENDANCE_RECORDS_IMPORT}?payrollPeriodId=${payrollPeriodId}&staffFullTimeId=${staffFullTimeId}&projectId=${projectId}`)}
                    >
                      {t('timesheet.import')}
                    </Button>
                  </>
                )
              }
              {
                getPermission(rolesUser, roles.SALARY_RECORD_INDEX) && (
                  <Button
                    style={{ borderColor: '#34C759', color: '#34C759' }}
                    onClick={() => navigate(`${ROUTE_PATHS.SALARY_CALCULATE_TABLE_DETAIL}?projectId=${projectId}&staffFullTimeId=${staffFullTimeId}&payrollPeriodId=${payrollPeriodId}`)}
                  >
                    {t('timesheet.salaryCalculate')}
                  </Button>
                )
              }
            </Space>
          )}
        >
          <div className="t-pagetable_wrap">
            <FormProvider {...method}>
              <Table<DataType>
                columns={columns}
                dataSource={method.getValues('users')}
                scroll={{ x: 'max-content' }}
                rowClassName={(row) => {
                  const classzIndexRow = row.id === editStaff?.id ? 'zIndexUp' : 'zIndexDown';
                  const classWarning = row.workingDateWarning ? 'salary_warning' : '';
                  return `${classzIndexRow} ${classWarning}`;
                }}
                bordered
                pagination={false}
                summary={(record) => {
                  const totalShiftCount = record.reduce((prev, cur) => {
                    return prev + cur.totalShiftCount
                  }, 0);

                  const merged = record.reduce((acc: Record<string, number>, item) => {
                    Object.entries(item).forEach(([key, value]) => {
                      if (dateKeyRegex.test(key)) {
                        acc[key] = (acc[key] || 0) + value;
                      }
                    });
                    return acc;
                  }, {});

                  const lengthTable = data && data?.length > 0 ? Object.keys(data[0].attendanceData).length + 5 : 5;

                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={4}>
                          <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                            Tổng:
                          </Typography.Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="center">
                          <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                            {totalShiftCount}
                          </Typography.Text>
                        </Table.Summary.Cell>
                        {
                          Object.entries(merged).map(([key, value], index) => (
                            <Table.Summary.Cell index={5 + index} align="center">
                              <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                                {value}
                              </Typography.Text>
                            </Table.Summary.Cell>
                          ))
                        }
                        <Table.Summary.Cell index={lengthTable} colSpan={2} />
                      </Table.Summary.Row>
                    </Table.Summary>
                  )
                }}
              />
            </FormProvider>
          </div>
        </Card>
        <Modal
          open={isNoteOpen}
          onCancel={() => {
            setIsNoteOpen(false);
            methodNote.reset({
              note: '',
              id: null
            });
          }}
          okButtonProps={{
            disabled: approved || !permission?.canDo
          }}
          cancelButtonProps={{
            disabled: approved || !permission?.canDo
          }}
          onOk={() => handleSubmitNote()}
          width={800}
          title={(
            <Typography.Title
              level={4}
            >
              {t('salary.note')}
            </Typography.Title>
          )}
        >
          <FormProvider {...methodNote}>
            <Controller
              name="note"
              render={({
                field: { value, onChange }, fieldState: { error }
              }) => (
                <>
                  <TextArea
                    className="u-mt-8"
                    value={value}
                    onChange={onChange}
                    size="large"
                    rows={6}
                    style={{ minHeight: 50 }}
                  />
                  {error && (
                    <span
                      className="a-input_errorMessage"
                    >
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </FormProvider>
        </Modal>
        <ProjectStaffJoinedModal
          handleClose={() => {
            setIsOpenProjectStaffJoined({
              isOpen: false,
              staffId: -1,
            });
          }}
          isOpen={isOpenProjectStaffJoined.isOpen}
          staffId={isOpenProjectStaffJoined.staffId}
          monthPayroll={monthPayroll}
        />
      </Spin>
    </div>
  );
};

export default TimeSheetTable;
